import React, { useState, useEffect } from 'react';

/**
 * CloseCaseDialog
 *
 * Post-publish modal shown when the backend determined CloseCase=true for any case.
 * Mirrors the old TriageSummaryPage.aspx closure workflow:
 * 1. User selects Closure Type (Discharged/Terminated)
 * 2. User selects Closure Reason (loaded from CaseStatusReasonList)
 * 3. User optionally adds comments
 * 4. Calls CaseStatusSave to close the case
 */
const CloseCaseDialog = ({ api, casesToClose, onComplete, onSkip }) => {
  const [closureTypeId, setClosureTypeId] = useState('2098'); // Default: Discharged
  const [reasons, setReasons] = useState([]);
  const [selectedReasonId, setSelectedReasonId] = useState('');
  const [comments, setComments] = useState('');
  const [loadingReasons, setLoadingReasons] = useState(true);
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState(null);
  const [closedCases, setClosedCases] = useState([]);

  useEffect(() => {
    loadReasons(closureTypeId === '2098' ? 'Discharged' : 'Terminated');
  }, [closureTypeId]);

  const loadReasons = async (statusDescription) => {
    try {
      setLoadingReasons(true);
      const result = await api.transact('CaseStatusReasonList', {
        statusdescription: statusDescription
      });
      const reasonList = result.apiResult || [];
      setReasons(reasonList);
      if (reasonList.length > 0) {
        setSelectedReasonId(reasonList[0].pkCaseStatusReasonID.toString());
      }
    } catch (err) {
      console.error('Error loading closure reasons:', err);
      setError('Failed to load closure reasons');
    } finally {
      setLoadingReasons(false);
    }
  };

  const handleCloseCase = async (caseToClose) => {
    try {
      setClosing(true);
      setError(null);
      await api.transact('CaseStatusSave', {
        CaseID: caseToClose.pkCaseID.toString(),
        StatusTypeID: closureTypeId,
        CaseStatusReasonID: selectedReasonId,
        Comments: comments
      });
      setClosedCases(prev => [...prev, caseToClose.pkCaseID]);
    } catch (err) {
      console.error('Error closing case:', err);
      setError('Failed to close case. Please try again.');
    } finally {
      setClosing(false);
    }
  };

  const allClosed = casesToClose.every(c => closedCases.includes(c.pkCaseID));

  return (
    <div className="close-case-overlay">
      <div className="close-case-dialog">
        <h3>Report Published Successfully</h3>

        {casesToClose.map(caseItem => {
          const isClosed = closedCases.includes(caseItem.pkCaseID);

          return (
            <div key={caseItem.pkCaseID} className="close-case-item">
              <h4>Case {caseItem.CaseID || caseItem.pkCaseID}</h4>

              {caseItem.InjuryList && caseItem.InjuryList.length > 0 && (
                <div className="close-case-injuries">
                  <p>Injuries:</p>
                  <ul>
                    {caseItem.InjuryList.map((injury, idx) => (
                      <li key={idx}>{injury.MedicalRegion} - {injury.TreatmentPathway}</li>
                    ))}
                  </ul>
                </div>
              )}

              {isClosed ? (
                <p className="close-case-closed-msg">This case has been closed.</p>
              ) : (
                <div className="close-case-form">
                  <p>You have chosen to close this case, please select a reason.</p>

                  <div className="close-case-field">
                    <label>Closure Type</label>
                    <select
                      value={closureTypeId}
                      onChange={(e) => setClosureTypeId(e.target.value)}
                      disabled={closing}
                    >
                      <option value="2098">Discharged</option>
                      <option value="2099">Terminated</option>
                    </select>
                  </div>

                  <div className="close-case-field">
                    <label>Closure Reason</label>
                    {loadingReasons ? (
                      <span>Loading reasons...</span>
                    ) : (
                      <select
                        value={selectedReasonId}
                        onChange={(e) => setSelectedReasonId(e.target.value)}
                        disabled={closing}
                      >
                        {reasons.map(reason => (
                          <option key={reason.pkCaseStatusReasonID} value={reason.pkCaseStatusReasonID}>
                            {reason.Description}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="close-case-field">
                    <label>Comments</label>
                    <textarea
                      value={comments}
                      onChange={(e) => setComments(e.target.value)}
                      maxLength={250}
                      rows={3}
                      disabled={closing}
                    />
                  </div>

                  <button
                    className="close-case-button"
                    onClick={() => handleCloseCase(caseItem)}
                    disabled={closing || loadingReasons || !selectedReasonId}
                  >
                    {closing ? 'Closing...' : 'Close Case'}
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {error && <div className="close-case-error">{error}</div>}

        <div className="close-case-actions">
          {allClosed ? (
            <button className="close-case-done-button" onClick={onComplete}>
              Done
            </button>
          ) : (
            <button className="close-case-skip-button" onClick={onSkip}>
              Skip - Return to Case
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CloseCaseDialog;
